<template>
  <section
    :style="sectionStyle"
    :class="sectionClass"
    :data-background-image="dataBgImage"
    class="background-image py-0 py-md-space-md py-lg-space-xxl">
    <div class="container px-0 px-lg-gutter">
      <div class="row gx-0">
        <div
          class="col-12 col-lg-6 description-img sif-lazy"
          :class="bgImageClass"
          :style="bgImageStyle"
          ref="image"
          :data-background-image="dataImageSrc">
          <div
            class="col-lg-12 px-space-xs py-space-md p-lg-space-lg my-lg-space-lg description-div"
            :class="containerBgClass">
            <h2 class="mb-space-md" :class="titleClass">{{ title }}</h2>

            <div class="text-start">
              <slot name="body"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import lozad from "lozad";
import { computed, onMounted, ref, watch } from "vue";

interface Props {
  title: string;
  titleVariant?: string;
  titleSize?: "xl" | "large" | "small" | "medium";
  imageSource: string;
  imagePosition?: "left" | "right";
  imageHighlightVariant?: string;
  bgImageSource?: string;
  containerBgVariant?: string;
  bgImageLazy?: boolean;
  sectionBackgroundVariant?: string;
  imageSourceLazy?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  titleVariant: "dark-3",
  titleSize: "medium",
  imageHighlightVariant: "secondary",
  imagePosition: "left",
  containerBgVariant: "white",
  sectionBackgroundVariant: "white",
  imageSourceLazy: true,
});

const { width: screenWidth } = useWindowSize();
const image = ref<HTMLImageElement>();

const observer: lozad.Observer | null = window ? lozad(".sif-lazy") : null;

const displayImage = computed(() => {
  return screenWidth.value >= 992;
});

const sectionStyle = computed(() => {
  if (!props.bgImageLazy && props.bgImageSource) {
    return { "background-image": `url('${props.bgImageSource}')` };
  } else {
    return null;
  }
});

const sectionClass = computed(() => {
  let classList = `bg-${props.sectionBackgroundVariant} `;
  props.bgImageLazy ? (classList += "sif-lazy ") : null;
  return classList;
});

const dataImageSrc = computed(() => {
  if (props.imageSourceLazy && props.imageSource) {
    return props.imageSource;
  } else {
    return null;
  }
});

const dataBgImage = computed(() => {
  if (props.bgImageLazy && props.bgImageSource) {
    return props.bgImageSource;
  } else {
    return null;
  }
});

const titleClass = computed(() => {
  return `h2-${props.titleSize} text-${props.titleVariant}`;
});

const bgImageStyle = computed(() => {
  let styles = "";
  if (displayImage.value) {
    if (!props.imageSourceLazy && props.imageSource) {
      styles += `background-image: url('${props.imageSource}'); `;
    }
    if (props.imagePosition == "left") {
      styles += `box-shadow: -50px 0 0 -35px var(--rds-${props.imageHighlightVariant}); `;
    } else {
      styles += `box-shadow: 50px 0 0 -35px var(--rds-${props.imageHighlightVariant}); `;
    }
  }

  return styles;
});

const bgImageClass = computed(() => {
  return props.imagePosition == "left" ? `image-side-left` : `image-side-right`;
});

const containerBgClass = computed(() => {
  return `bg-${props.containerBgVariant}`;
});

// makes sure we re-load the image if the screen goes from mobile to desktop
watch(
  displayImage,
  (newValue) => {
    if (
      newValue === true &&
      observer &&
      props.imageSourceLazy &&
      typeof window !== "undefined" &&
      window !== null
    ) {
      image.value?.removeAttribute("data-loaded");
      observer.triggerLoad(image.value!);
    }
  },
  // triggering post to make sure lozad gets the new attribute value from the dom
  { flush: "post" }
);

onMounted(() => {
  if (props.bgImageLazy || props.imageSourceLazy) {
    observer !== null ? observer.observe() : null;
  }
});
</script>

<style lang="scss" scoped>
/* Large devices (desktops, 992px and up) */

@media (min-width: 992px) {
  .description-img {
    background-position: center top;
    background-repeat: no-repeat;
    background-size: cover;
    margin-right: 15px;
    min-height: 350px;

    &.image-side-right {
      margin-left: 43.33333%;
    }
  }
  .description-div {
    .image-side-right & {
      margin-left: -83.3333%;
    }
    .image-side-left & {
      margin-left: 83.3333%;
    }
  }
}
</style>
